import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X, Check, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { formatBizDateTime } from "../../lib/formatBiz";
import { T } from "../../lib/testIds";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [services, setServices] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ contact_id: "", service_id: "", scheduled_at: "", address: "", value: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/jobs").then(r => setJobs(r.data)), []);
  useEffect(() => {
    load();
    api.get("/contacts").then(r => setContacts(r.data));
    api.get("/services").then(r => setServices(r.data));
  }, [load]);

  const create = async () => {
    if (!form.contact_id || !form.service_id || !form.scheduled_at) return toast.error("Fill in all fields");
    setSaving(true);
    try {
      await api.post("/jobs", { ...form, scheduled_at: new Date(form.scheduled_at).toISOString(), value: Number(form.value) });
      toast.success("Job created");
      setAddOpen(false); setForm({ contact_id: "", service_id: "", scheduled_at: "", address: "", value: 0 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const complete = async (id) => {
    try { await api.patch(`/jobs/${id}`, { status: "completed" }); toast.success("Marked complete + thank-you sent"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this job? Customer will be notified.")) return;
    try { await api.patch(`/jobs/${id}`, { status: "cancelled" }); toast.success("Job cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const contactName = (id) => contacts.find(c => c.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Jobs</h1>
          <p className="text-slate-600 mt-1">Every scheduled, active, and completed job.</p>
        </div>
        <button data-testid={T.jobs.addBtn} onClick={() => setAddOpen(true)} className="owc-btn-accent text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New job</button>
      </div>

      <div className="owc-card p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-bold uppercase text-slate-500">
              <th className="py-3">When</th><th>Service</th><th>Customer</th><th>Address</th><th>Value</th><th>Payment</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {jobs.map(j => {
              const pstyle = {
                paid_in_full: "bg-emerald-50 text-emerald-700 border-emerald-200",
                deposit_paid: "bg-sky-50 text-sky-700 border-sky-200",
                deposit_pending: "bg-amber-50 text-amber-700 border-amber-200",
                unpaid: "bg-slate-50 text-slate-600 border-slate-200",
              }[j.payment_status || "unpaid"] || "bg-slate-50 text-slate-600 border-slate-200";
              const plabel = { paid_in_full: "Paid", deposit_paid: "Deposit paid", deposit_pending: "Deposit pending", unpaid: "Unpaid" }[j.payment_status || "unpaid"] || j.payment_status;
              return (
              <tr key={j.id} data-testid={T.jobs.row(j.id)} className="hover:bg-slate-50">
                <td className="py-3 pr-4 font-semibold text-[color:var(--owc-navy)]">{formatBizDateTime(j.scheduled_at)}</td>
                <td className="py-3 pr-4">{j.service_name}</td>
                <td className="py-3 pr-4"><Link className="hover:text-[color:var(--owc-sky)]" to={`/admin/contacts/${j.contact_id}`}>{contactName(j.contact_id)}</Link></td>
                <td className="py-3 pr-4 text-slate-600 max-w-[220px] truncate">{j.address}</td>
                <td className="py-3 pr-4 font-semibold">${(j.value || 0).toFixed(0)}</td>
                <td className="py-3 pr-4"><span data-testid={`job-payment-${j.id}`} className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border ${pstyle}`}>{plabel}</span></td>
                <td className="py-3 pr-4"><span className={`owc-badge-${j.status === "completed" ? "green" : j.status === "cancelled" ? "red" : "blue"}`}>{j.status}</span></td>
                <td className="py-3 flex gap-1">
                  {j.status !== "completed" && j.status !== "cancelled" && (
                    <>
                      <button data-testid={T.jobs.complete(j.id)} onClick={() => complete(j.id)} className="p-1.5 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50" title="Mark complete"><Check className="h-3.5 w-3.5" /></button>
                      <button data-testid={T.jobs.cancel(j.id)} onClick={() => cancel(j.id)} className="p-1.5 rounded border border-red-200 text-red-700 hover:bg-red-50" title="Cancel"><XCircle className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                </td>
              </tr>
            );})}
            {jobs.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-500">No jobs yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="owc-card-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-[Outfit] font-bold text-lg">New job</h3>
              <button onClick={() => setAddOpen(false)}><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <select className="owc-input" value={form.contact_id} onChange={e => setForm(f => ({ ...f, contact_id: e.target.value }))}>
                <option value="">Choose contact…</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select className="owc-input" value={form.service_id} onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))}>
                <option value="">Choose service…</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input type="datetime-local" className="owc-input" value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} />
              <input className="owc-input" placeholder="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
              <input type="number" className="owc-input" placeholder="Value ($)" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button className="owc-btn-outline text-sm" onClick={() => setAddOpen(false)}>Cancel</button>
              <button disabled={saving} className="owc-btn-accent text-sm inline-flex items-center gap-2" onClick={create}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create job</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
